import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';

import Input, { InputType, InputProps } from 'ui/Input';

import styles from './index.module.scss';

export type TimeInputProps = InputProps & {
  pattern: RegExp;
  mask: string;
  defaultValue?: string;
  resetToDefaultValueIfError?: boolean;
  inputValidator?: (value: string, prevValue: string) => string;
  onError?: (message: string) => void;
};

export const TIME_PATTERN = /^(?:\d|[01]\d|2[0-3]):[0-5]\d$/;

const DEFAULT_MAX_LENGTH = 5;

export const defaultTimeInputValidator = (value: string, prevValue: string): string => {
  if (/[a-zA-Zа-яА-ЯйЙ]/g.test(value)) {
    return prevValue;
  }

  if (value[0] === ':') {
    return value;
  }

  const replaced = value.replace(/[^\d:]/g, '');

  if (replaced.length === 3 && replaced[replaced.length - 1] !== ':' && replaced[replaced.length - 2] !== ':') {
    return `${replaced.substr(0, replaced.length - 1)}:${replaced[replaced.length - 1]}`;
  }

  if (replaced[replaced.length - 1] === ':' && value.length >= replaced.length) {
    return replaced.substring(0, 2);
  }

  if (value[value.length - 1] === ':' && value.length >= replaced.length) {
    return replaced;
  }

  if (replaced.match(/^\d{2}$/) && prevValue.length === 1) {
    return `${replaced}:`;
  }

  if (replaced[replaced.length - 1] === ':' && replaced[replaced.length - 2] === ':') {
    return replaced.slice(0, -1);
  }

  if (replaced[replaced.length - 2] === ':' && replaced.length === 5) {
    return prevValue;
  }

  return replaced;
};

const TimeInput = ({
  elementId,
  value: propValue,
  mask,
  onChange,
  onFocus,
  onBlur,
  onError,
  label,
  isDisabled,
  isLoading,
  defaultValue,
  pattern,
  resetToDefaultValueIfError,
  inputValidator,
  error,
  placeholder,
  containerClassName,
  forceError,
  maxLength = DEFAULT_MAX_LENGTH,
}: TimeInputProps) => {
  const [inputValue, setInputValue] = useState<string | undefined>(propValue);
  const inputMask = useRef<HTMLInputElement>(null);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (inputMask.current) {
      const { value } = inputMask.current;

      if (!value.match(pattern)) {
        if (defaultValue && resetToDefaultValueIfError) {
          setInputValue(defaultValue);
          onChange?.(defaultValue, e);
        } else {
          setInputValue(propValue);
          onError?.(`Введите значение в формате ${mask}`);
          onChange?.(propValue!, e);
        }
      }
    }

    onBlur?.(e);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
    onError?.(`Введите значение в формате ${mask}`);
    onFocus?.(e);
  };

  const handleChange = (value: string, e: React.SyntheticEvent) => {
    const newValue = inputValidator ? inputValidator(value, propValue!) : value;
    setInputValue(newValue);
    onChange?.(newValue, e);
  };

  const handleEnterPressed = (_: React.KeyboardEvent<HTMLInputElement>) => {
    if (inputMask.current) {
      inputMask.current.blur();
    }
  };

  useEffect(() => {
    if (propValue !== inputValue) {
      setInputValue(propValue);
    }
  }, [propValue]);

  return (
    <Input
      elementId={elementId}
      type={InputType.TEXT}
      ref={inputMask}
      value={inputValue}
      containerClassName={classNames(styles.TimeInput, containerClassName)}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onEnterPressed={handleEnterPressed}
      label={label}
      isDisabled={isDisabled}
      isLoading={isLoading}
      placeholder={placeholder}
      error={error}
      maxLength={maxLength}
      forceError={forceError}
    />
  );
};

export default TimeInput;
